import { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import type {
  StorageAddressRef,
  InventoryTableRow,
  TransferPayload,
} from "../ProductsTable/types";

interface StockTransferModalProps {
  open: boolean;
  product: InventoryTableRow;
  storageAddresses: StorageAddressRef[];
  /** Address pre-selected as origin (from the row the user clicked) */
  defaultFromAddressId: number;
  onClose: () => void;
  /** Called with a validated TransferPayload when the user confirms */
  onTransfer: (payload: TransferPayload) => Promise<void>;
}

/**
 * Modal dialog to transfer stock from one storage address to another.
 * Validates client-side before calling the backend:
 *  - quantity must be > 0
 *  - origin must differ from destination
 */
export function StockTransferModal({
  open,
  product,
  storageAddresses,
  defaultFromAddressId,
  onClose,
  onTransfer,
}: StockTransferModalProps) {
  const [fromAddressId, setFromAddressId] = useState<number>(
    defaultFromAddressId || (storageAddresses[0]?.id ?? 0)
  );
  const [toAddressId, setToAddressId] = useState<number>(
    storageAddresses[0]?.id ?? 0
  );
  const [quantity, setQuantity] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Client-side validation gates
  const isQuantityValid = Number.isInteger(quantity) && quantity > 0;
  const isAddressDifferent = fromAddressId !== toAddressId;
  const canConfirm = isQuantityValid && isAddressDifferent;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    try {
      await onTransfer({
        product_id: product.id,
        from_address_id: fromAddressId,
        to_address_id: toAddressId,
        quantity,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setQuantity(Number.isNaN(value) ? 0 : value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trasladar stock — {product.name}</DialogTitle>

      <DialogContent>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel id="from-address-label">Origen</InputLabel>
          <Select
            labelId="from-address-label"
            inputProps={{ "data-testid": "from-address-select" }}
            value={fromAddressId}
            label="Origen"
            onChange={(e) => setFromAddressId(e.target.value as number)}
          >
            {storageAddresses.map((addr) => (
              <MenuItem key={addr.id} value={addr.id}>
                {addr.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel id="to-address-label">Destino</InputLabel>
          <Select
            labelId="to-address-label"
            inputProps={{ "data-testid": "to-address-select" }}
            value={toAddressId}
            label="Destino"
            onChange={(e) => setToAddressId(e.target.value as number)}
          >
            {storageAddresses.map((addr) => (
              <MenuItem key={addr.id} value={addr.id}>
                {addr.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Cantidad"
          type="number"
          variant="outlined"
          fullWidth
          margin="normal"
          value={quantity === 0 ? "" : quantity}
          onChange={handleQuantityChange}
          inputProps={{
            min: 1,
            step: 1,
            "aria-label": "cantidad",
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!canConfirm || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StockTransferModal;
